import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { fetchApi, buildQueryString } from "@/lib/api/client";
import { recipientsFetch } from '@/lib/recipients/api';
import { toastRecipientRefusal } from "@/lib/recipients/refusal-codes";
import { syncWillReactivateOf, toastSyncWillReactivate } from "@/lib/recipients/sync-reactivation";
import { useTranslation } from "@/lib/i18n";
import {
  recipientKeys,
  tagKeys,
  groupKeys,
  recycleBinKeys,
} from "@/hooks/api/query-keys";
import { orgChartKeys } from "./use-org-chart";
import { lmsLearnerKeys } from "@/components/lms/hooks/lms-learner-keys";
import type {
  Recipient,
  RecipientList,
  RecipientsMetrics,
} from "@/lib/types/recipients";
import type { BulkTarget } from "@/lib/types/bulk";
import { normalizeRecipient } from "@/lib/utils/normalize-recipient";
import { toServerSortKey } from "@/lib/recipients/sort-keys";
import type { PaginatedResult } from "@/hooks/api/pagination";
export { normalizeRecipient };
export type { BulkTarget };

/**
 * PROD-1606: every recipient change marks the LMS learner cache stale. The
 * roster that needed this went with the Enrol dialog and nothing reads the key
 * today: the enrol drawer lists people through useRecipients and asks the server
 * the rest.
 */
function invalidateLmsLearners(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: lmsLearnerKeys.all() });
}

/**
 * #797f — how long the dynamic-group refresh a save queues has to run. The API
 * queues it with no delay and the worker (GroupRefreshProcessor) takes five
 * groups at a time, so it is usually done within seconds.
 */
const DYNAMIC_GROUP_REFRESH_SETTLE_MS = 5_000;

/**
 * #797f — a change a dynamic group's rule reads moves the person in or out of
 * that group, but in the worker, after the save has answered: a re-read right
 * after the save comes too early for the profile's Groups card. Read the
 * person's groups once more when the refresh has had time.
 */
function rereadGroupsAfterRefresh(queryClient: ReturnType<typeof useQueryClient>, id: string) {
  setTimeout(() => {
    queryClient.invalidateQueries({ queryKey: [...recipientKeys.detail(id), "groups"] });
  }, DYNAMIC_GROUP_REFRESH_SETTLE_MS);
}

function toRecipientWriteDto(data: Partial<Recipient>): Record<string, unknown> {
  const { customFields, ...rest } = data;
  return {
    ...rest,
    ...(customFields !== undefined &&
    (rest as Record<string, unknown>).customFieldValues === undefined
      ? { customFieldValues: customFields }
      : {}),
  };
}

// ============ Filter Types ============

export interface RecipientFilter {
  page?: number;
  limit?: number;
  search?: string;
  /** Only these recipients (GET /recipients `ids`, at most 200 — FILTER_FACET_MAX_SIZE). */
  ids?: string[];
  status?: string | string[];
  departments?: string[];
  designations?: string[];
  locations?: string[];
  countries?: string[];
  employmentTypes?: string[];
  domainId?: string;
  tagIds?: string[];
  groupIds?: string[];
  sources?: string[];
  isRemote?: string[];
  /** Only people whose mailbox no longer exists (the API set them Inactive and recorded why). The name and the
   *  value are in lib/recipients/mailbox-gone.ts (MAILBOX_GONE_QUERY_PARAM / _VALUE). */
  mailboxGone?: string;
  /** Profile completion bucket(s) — 'Complete' | 'Partial' | 'Incomplete'.
   *  Mirrors the FE calculateCompleteness() buckets; BE computes the
   *  filled-field count in SQL. */
  completeness?: string[];
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  /** Bug #143 — ISO start/end for createdAt server-side filtering. */
  from?: string;
  to?: string;
  /** Bug #143 — ISO start/end for updatedAt server-side filtering. */
  updatedFrom?: string;
  updatedTo?: string;
  /** #62 — server-side range over a Date CUSTOM FIELD (Hire Date and friends).
   *  Bounds are calendar dates (YYYY-MM-DD), not ISO timestamps: the value is
   *  stored as a plain date string in JSONB, and going through UTC would shift
   *  the boundary a day for anyone not on UTC. */
  cfDateKey?: string;
  cfDateFrom?: string;
  cfDateTo?: string;
  /** Bug #45 — JSON-stringified `{field, operator, value}[]` clauses passed
   *  through to the backend when no legacy set-valued fast path applies. */
  advancedFilters?: string;
}

// ============ Query Hooks ============

/**
 * The query string GET /recipients is sent for a list filter. One serialiser:
 * the list uses it, and so does anything that must ask the list's exact
 * question (the Advanced Search preview count).
 */
export function recipientsListQueryString(filter?: RecipientFilter): string {
  if (!filter) return "";
  // #409 — the FE column id is not the API sort key. "name" → firstName was
  // the only translation that existed; every other id (location,
  // completeness, hireDate, cf_*, …) went out raw and 400'd the request.
  // A column the API cannot order by now sends NO sort at all — a
  // needlessly unsorted list beats an empty one.
  const serverSortKey = toServerSortKey(filter.sortBy);
  const normalizedFilter: Record<string, unknown> = {
    ...filter,
    // Backend expects status as array
    status: filter.status
      ? Array.isArray(filter.status)
        ? filter.status
        : [filter.status]
      : undefined,
    sortBy: serverSortKey ?? undefined,
    sortOrder: serverSortKey ? filter.sortOrder : undefined,
  };
  return buildQueryString(normalizedFilter);
}

export function useRecipients(filter?: RecipientFilter) {
  const defaultLimit = filter?.limit ?? 25;
  return useQuery({
    queryKey: recipientKeys.list(filter as Record<string, unknown>),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const qs = recipientsListQueryString(filter);
      return fetchApi<
        | {
            data: Recipient[];
            total: number;
            page: number;
            limit: number;
            totalPages?: number;
            unfilteredTotal?: number;
          }
        | Recipient[]
      >(`/recipients${qs}`);
    },
    select: (raw): PaginatedResult<Recipient> => {
      if (Array.isArray(raw)) {
        return {
          data: raw.map((r) =>
            normalizeRecipient(r as unknown as Record<string, unknown>),
          ),
          total: raw.length,
          page: 1,
          limit: raw.length || defaultLimit,
          totalPages: 1,
          unfilteredTotal: raw.length,
        };
      }
      const limit = raw.limit ?? defaultLimit;
      const total = raw.total ?? 0;
      return {
        data: (raw.data ?? []).map((r) =>
          normalizeRecipient(r as unknown as Record<string, unknown>),
        ),
        total,
        page: raw.page ?? 1,
        limit,
        totalPages: raw.totalPages ?? Math.max(1, Math.ceil(total / limit)),
        // ticket #347 — distinguishes filtered-empty from truly-empty.
        // Falls back to `total` when the BE hasn't shipped the field yet.
        unfilteredTotal: raw.unfilteredTotal ?? total,
      };
    },
  });
}

export function useRecipient(id: string | undefined) {
  return useQuery({
    queryKey: recipientKeys.detail(id!),
    queryFn: () => recipientsFetch<Record<string, unknown>>(`${id}`),
    enabled: !!id,
    select: (raw) => normalizeRecipient(raw),
  });
}

// Per-recipient phishing funnel summary — drives the View-As portal preview
// phishing cards (BUG-513). Backed by GET /v1/recipients/:id/phishing-summary.
export interface RecipientPhishingSummary {
  sent: number
  opened: number
  clicked: number
  compromised: number
  reported: number
  trained: number
  /** Trainings sent (STG-654; one per campaign target sent training) — trained ⊆ it (a Prior Completion Credit counts). */
  trainingSent: number
  /** Trainings the person had to TAKE (27 Oct) — evaluated ⊆ it; a credit is not one. */
  trainingToTake: number
  /** completed (passed the quiz) ÷ trainings to take, whole percent, computed by the API (STG-654; 27 Oct). */
  trainingCompletionRate: number
  evaluated: number
  campaignCount: number
  lastIncident: { campaignName: string; date: string } | null
}

export function useRecipientPhishingSummary(id: string | undefined) {
  return useQuery({
    queryKey: [...recipientKeys.detail(id!), 'phishing-summary'] as const,
    queryFn: () =>
      recipientsFetch<RecipientPhishingSummary>(`${id}/phishing-summary`),
    enabled: !!id,
  })
}

/**
 * KPI-card metrics for the recipient list.
 *
 * #61 — this took a bare `domainId`, so the cards moved for a Domain filter
 * (Bug #113) and stood still for search, advanced conditions and Date Added:
 * "Showing 2" above four cards reading 10. It now takes the SAME filter object
 * the list query uses, minus pagination/sort/status — `status` is excluded by
 * the caller because these counts also feed the status dropdown, which
 * disables zero-count options. The cards keep the chosen statuses' counts
 * (#825, statusKpiCounts).
 *
 * The key stays under `recipientKeys.metricsAll()` so every existing
 * `invalidateQueries({ queryKey: recipientKeys.metricsAll() })` still clears it.
 */
export function useRecipientMetrics(filter?: Record<string, unknown>) {
  return useQuery({
    queryKey: [...recipientKeys.metricsAll(), filter ?? null] as const,
    placeholderData: keepPreviousData,
    queryFn: () => {
      const qs = filter ? buildQueryString(filter) : "";
      return recipientsFetch<RecipientsMetrics>(`metrics${qs}`);
    },
  });
}

export function useRecipientFilterOptions() {
  return useQuery({
    queryKey: recipientKeys.filterOptions(),
    queryFn: () =>
      fetchApi<{
        /** Every stored department value, as typed — what other screens match on. */
        departments: string[];
        /** The recipients list's Department filter: one label per group, "No Department" last. */
        departmentGroups: string[];
        designations: string[];
        locations: string[];
        countries: string[];
        statuses: string[];
        employmentTypes: string[];
        sources: string[];
      }>("/recipients/filter-options"),
    // Distinct field values (departments / designations / locations / …) power
    // the group-rule value pickers. They change only when recipients are
    // added/edited, so cache for 5 minutes; a recipient create / import / bulk
    // update invalidates recipientKeys.filterOptions() to bust it early, giving
    // "5 minutes, or on new values".
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Distinct PhishTrainer enrolment values (risk band / seniority / status, plus
 * department/designation/location the PT list shares) for rule-builder combo
 * boxes — the PhishTrainer analogue of useRecipientFilterOptions. Both the
 * group builder and the workflow rule builder upgrade the pt_* free-text fields
 * to a searchable select from these real stored values. `retry: false` because
 * PhishTrainer may be disabled for the tenant (a 403 → undefined → the pt_*
 * fields simply stay free text).
 */
export function usePhishtrainerFilterOptions() {
  return useQuery({
    queryKey: ["phishtrainer", "users", "filter-options"],
    queryFn: () =>
      fetchApi<{
        departments: string[];
        designations: string[];
        locations: string[];
        seniorities: string[];
        riskBands: string[];
        statuses: string[];
        tags: string[];
      }>("/phishtrainer/users/filter-options"),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });
}

export function useRecipientCount(filter?: RecipientFilter) {
  return useQuery({
    queryKey: recipientKeys.count(filter as Record<string, unknown>),
    queryFn: async () => {
      const qs = filter
        ? buildQueryString(filter as Record<string, unknown>)
        : "";
      return recipientsFetch<{ count: number }>(`count${qs}`);
    },
  });
}

export function useRecipientDuplicates() {
  return useQuery({
    queryKey: recipientKeys.duplicates(),
    queryFn: () => recipientsFetch<Recipient[][]>("duplicates"),
  });
}

export function useRecipientGroups(id: string | undefined) {
  return useQuery({
    queryKey: [...recipientKeys.detail(id!), "groups"],
    queryFn: () => recipientsFetch<RecipientList[]>(`${id}/groups`),
    enabled: !!id,
  });
}

export function useRecipientTags(id: string | undefined) {
  return useQuery({
    queryKey: [...recipientKeys.detail(id!), "tags"],
    queryFn: () => recipientsFetch<string[]>(`${id}/tags`),
    enabled: !!id,
  });
}

export function useRecipientHistory(id: string | undefined) {
  return useQuery({
    queryKey: [...recipientKeys.detail(id!), "history"],
    queryFn: () =>
      fetchApi<
        {
          id: string;
          action: string;
          field?: string;
          oldValue?: string;
          newValue?: string;
          createdAt: string;
          createdBy: string;
        }[]
      >(`/recipients/${id}/history`),
    enabled: !!id,
  });
}

/**
 * A single workflow-execution event for a recipient, mapped from the recipient
 * audit trail (`GET /recipients/:id/history` → entityType='Recipient' rows the
 * Workflows engine writes on each successful action). Surfaces the per-recipient
 * workflow trail in the profile Activity timeline (feedback #73/#74).
 */
export interface RecipientWorkflowActivity {
  id: string;
  /** 'workflow_email_sent' | 'workflow_action_executed' (raw audit action). */
  rawAction: string;
  /** Internal action token, e.g. 'notify.email' | 'lms.enroll_course'. */
  actionType: string;
  at: string; // ISO timestamp
}

/**
 * Recipient Workflows activity — the per-recipient workflow-execution trail the
 * profile Activity timeline merges with phishing-campaign events. Reads the
 * shared recipient audit-history endpoint and keeps ONLY the Workflows-module
 * rows (rawAction starts with `workflow_`); phishing/recipient-CRUD rows are
 * already covered by other feeds. Normalized at this hook layer so the page
 * consumer receives a clean typed array.
 */
export function useRecipientWorkflowActivity(id: string | undefined) {
  return useQuery({
    queryKey: [...recipientKeys.detail(id!), "workflow-activity"],
    // FE18 (audit 2026-09-25) — the Workflows rows only (`module=Workflows`),
    // every page of them. This read the latest 100 rows of EVERY kind and
    // filtered here, so 100 newer ordinary rows (a nightly sync touching the
    // recipient is enough) pushed every workflow action off the timeline.
    queryFn: async () => {
      type Row = {
        id?: string;
        rawAction?: string;
        action?: string;
        timestamp?: string;
        createdAt?: string;
        metadata?: Record<string, unknown>;
      };
      const PAGE_SIZE = 200;
      const data: Row[] = [];
      for (let page = 1; page <= 100; page++) {
        const res = await fetchApi<{ data: Row[]; total?: number }>(
          `/recipients/${id}/history?module=Workflows&page=${page}&pageSize=${PAGE_SIZE}`,
        );
        const rows = res?.data ?? [];
        data.push(...rows);
        if (rows.length < PAGE_SIZE || data.length >= (res?.total ?? 0)) break;
      }
      return { data };
    },
    enabled: !!id,
    select: (res): RecipientWorkflowActivity[] => {
      const rows = res?.data ?? [];
      return rows
        .filter((r) => (r.rawAction ?? "").startsWith("workflow_"))
        .map((r) => ({
          id: r.id ?? `${r.rawAction}:${r.timestamp ?? r.createdAt ?? ""}`,
          rawAction: r.rawAction ?? "",
          actionType:
            typeof r.metadata?.actionType === "string"
              ? (r.metadata.actionType as string)
              : "",
          at: r.timestamp ?? r.createdAt ?? "",
        }))
        .filter((e) => e.at !== "");
    },
  });
}

/**
 * #936 + status report issue 13 — one row of the person's OWN history: what
 * the Recipients module recorded about them (added, edited, binned,
 * restored, merged), their status changes with the status before and after,
 * and their Suppression List rows (the API finds those by e-mail).
 */
export interface RecipientOwnActivity {
  id: string;
  /** The stored action: create | update | delete | recipient_restored | merge | … */
  rawAction: string;
  /** Recipient, or Suppression for a Suppression List row. */
  entityType: string;
  at: string;
  /** Who made the change, as the row names them. */
  by: string;
  /** How: screen | ai-chat | bulk | sync | import | api (metadata.via). */
  via?: string;
  /** A directory sync's source (metadata.source), e.g. "Okta". */
  source?: string;
  statusFrom?: string;
  statusTo?: string;
  /** Fields the row has a before and an after for. */
  changedFields: string[];
  /** Fields a row with no before carried (the generic 'Updated' row of a save). */
  editedFields: string[];
}

/**
 * #936 — the person's own changes for the profile's Activity tab, every page
 * of them (module=Recipients). The Workflows rows have their own hook above.
 */
export function useRecipientOwnActivity(id: string | undefined) {
  return useQuery({
    queryKey: [...recipientKeys.detail(id!), "own-activity"],
    queryFn: async () => {
      type Row = {
        id?: string;
        rawAction?: string;
        entityType?: string;
        timestamp?: string;
        createdAt?: string;
        userName?: string;
        changes?: Array<{ field?: string; oldValue?: unknown; newValue?: unknown }>;
        editedFields?: string[];
        metadata?: Record<string, unknown>;
      };
      const PAGE_SIZE = 200;
      const data: Row[] = [];
      for (let page = 1; page <= 100; page++) {
        const res = await fetchApi<{ data: Row[]; total?: number }>(
          `/recipients/${id}/history?module=Recipients&page=${page}&pageSize=${PAGE_SIZE}`,
        );
        const rows = res?.data ?? [];
        data.push(...rows);
        if (rows.length < PAGE_SIZE || data.length >= (res?.total ?? 0)) break;
      }
      return { data };
    },
    enabled: !!id,
    select: (res): RecipientOwnActivity[] =>
      (res?.data ?? [])
        .map((r) => {
          const changes = (r.changes ?? []).filter((c) => typeof c.field === "string");
          const status = changes.find((c) => c.field === "status");
          const text = (v: unknown) => (typeof v === "string" ? v : undefined);
          return {
            id: r.id ?? `${r.rawAction}:${r.timestamp ?? r.createdAt ?? ""}`,
            rawAction: r.rawAction ?? "",
            entityType: r.entityType ?? "",
            at: r.timestamp ?? r.createdAt ?? "",
            by: r.userName ?? "",
            via: text(r.metadata?.via),
            source: text(r.metadata?.source),
            statusFrom: status ? text(status.oldValue) : undefined,
            statusTo: status ? text(status.newValue) : undefined,
            changedFields: changes.map((c) => c.field as string),
            editedFields: (r.editedFields ?? []).filter((f) => typeof f === "string"),
          };
        })
        .filter((e) => e.at !== ""),
  });
}

// ============ Mutation Hooks ============
//
// Each hook says a refusal ONCE (toastRecipientRefusal): what failed, and why
// in the admin's language — never the API's English `message` (CLAUDE.md
// rule 9). The screens that call them put their values back and add no toast
// of their own; the org chart's "Set manager" relies on this one.

export function useCreateRecipient() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: Partial<Recipient>) =>
      recipientsFetch<Recipient>("", {
        method: "POST",
        body: JSON.stringify(toRecipientWriteDto(data)),
      }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Adding a recipient", "toast.failedCreateRecipient", err),
  });
}

export function useUpdateRecipient() {
  const queryClient = useQueryClient();
  const { t, locale } = useTranslation();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Recipient> }) =>
      recipientsFetch<Recipient>(`${id}`, {
        method: "PUT",
        body: JSON.stringify(toRecipientWriteDto(data)),
      }),
    onSuccess: (updated, { id, data }) => {
      // Recipient status report, issue 1: a switch-off says when the next
      // directory sync will switch the person back on (the API decides; this
      // covers the table switch, the drawer and the profile page).
      if (data.status === "Inactive") {
        const note = syncWillReactivateOf(updated);
        if (note) toastSyncWillReactivate(t, locale, note);
      }
      invalidateLmsLearners(queryClient);
      // #787 — re-read the profile; never cache the PUT answer as it. The
      // update answers with the row alone (`tags: []`, `groups: []`), and the
      // profile's Tags card reads this cache: it went blank, and the next tag
      // save — POST /:id/tags REPLACES the set — deleted every other tag.
      queryClient.invalidateQueries({ queryKey: recipientKeys.detail(id) });
      rereadGroupsAfterRefresh(queryClient, id);
      queryClient.invalidateQueries({ queryKey: recipientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.metricsAll() });
      // Org chart consumes its own list+tree endpoint; invalidate so an
      // edit made from any sidepanel (table OR org-chart OR future
      // consumers) re-renders the chart's department breakdown, tree
      // edges, and dimension summaries on the next visit.
      queryClient.invalidateQueries({ queryKey: orgChartKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Updating a recipient", "toast.failedSaveChange", err),
  });
}

export function useUpdateRecipientTags() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, tagIds }: { id: string; tagIds: string[] }) =>
      recipientsFetch<{ id: string; name: string; color: string | null }[]>(
        `${id}/tags`,
        {
          method: "POST",
          body: JSON.stringify({ tagIds }),
        },
      ),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: recipientKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: recipientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
      // Same reason as useUpdateRecipient — keep the org chart in sync.
      queryClient.invalidateQueries({ queryKey: orgChartKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Saving a recipient's tags", "toast.failedSaveTags", err),
  });
}

export function useDeleteRecipient() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) =>
      recipientsFetch<void>(`${id}`, { method: "DELETE" }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
      queryClient.invalidateQueries({ queryKey: recycleBinKeys.all() });
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Moving a recipient to the Recycle Bin", "toast.failedDelete", err),
  });
}

export function useBulkDeleteRecipients() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (target: BulkTarget | string[]) => {
      // Backward-compat: plain string[] → Mode 1 { ids }
      const payload: BulkTarget = Array.isArray(target)
        ? { ids: target }
        : target;
      return recipientsFetch<{ deleted: number }>("bulk-delete", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
      queryClient.invalidateQueries({ queryKey: recycleBinKeys.all() });
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Moving recipients to the Recycle Bin", "toast.failedBulkDelete", err),
  });
}

export function useBulkUpdateStatus() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (
      data:
        | (BulkTarget & { status: string })
        | { ids: string[]; status: string },
    ) =>
      recipientsFetch<{ affected: number }>("bulk-status", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Changing recipients' status", "toast.failedBulkStatus", err),
  });
}

export function useBulkUpdateField() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (
      data:
        | (BulkTarget & { field: string; value: unknown })
        | { ids: string[]; field: string; value: unknown },
    ) =>
      recipientsFetch<{ affected: number }>("bulk-update", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error, variables) =>
      toastRecipientRefusal(
        t,
        "Updating a field on recipients",
        // #791: a refused bulk Set Manager says what failed.
        variables.field === "managerId" ? "toast.failedBulkSetManager" : "toast.failedBulkUpdateField",
        err,
      ),
  });
}

export function useBulkCreateRecipients() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (recipients: Partial<Recipient>[]) =>
      recipientsFetch<{ created: number; errors: number }>(
        "bulk",
        {
          method: "POST",
          body: JSON.stringify({ recipients }),
        },
      ),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Adding recipients", "toast.failedBulkCreateRecipients", err),
  });
}

export function useMergeRecipients() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (data: {
      primaryId: string;
      secondaryIds: string[];
      strategy?: Record<string, 'primary' | 'secondary'>;
      mergeCustomFields?: boolean;
    }) => {
      if (data.secondaryIds.length === 0) {
        throw new Error('At least one secondary recipient is required')
      }

      let merged: Recipient | undefined
      for (const secondaryRecipientId of data.secondaryIds) {
        merged = await recipientsFetch<Recipient>("merge", {
          method: "POST",
          body: JSON.stringify({
            primaryRecipientId: data.primaryId,
            secondaryRecipientId,
            fieldResolution: data.strategy,
            mergeCustomFields: data.mergeCustomFields,
          }),
        })
      }
      return merged as Recipient
    },
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Merging recipients", "toast.failedMergeRecipients", err),
  });
}

export function useReplaceRecipientTags() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, tagIds }: { id: string; tagIds: string[] }) =>
      recipientsFetch<Recipient>(`${id}/tags`, {
        method: "POST",
        body: JSON.stringify({ tagIds }),
      }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: recipientKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Saving a recipient's tags", "toast.failedSaveTags", err),
  });
}

export function useUpdateCustomFields() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({
      id,
      customFieldValues,
    }: {
      id: string;
      customFieldValues: Record<string, unknown>;
    }) =>
      recipientsFetch<Recipient>(`${id}/custom-fields`, {
        method: "PATCH",
        body: JSON.stringify({ customFieldValues }),
      }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: recipientKeys.detail(id) });
      // The list drawer writes through here too (FE1), and its rows must
      // re-read the stored, merged values — as they did after the PUT this
      // replaced.
      queryClient.invalidateQueries({ queryKey: recipientKeys.lists() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Saving a recipient's custom field", "toast.failedSaveCustomField", err),
  });
}

/** Maximum emails resolved in a single bulk-paste lookup (degraded path). */
const RESOLVE_EMAIL_CAP = 20

/**
 * Resolve a list of email addresses to recipient records.
 *
 * NOTE: The BE does not yet expose POST /recipients/resolve.
 * This hook uses a serial search fallback (capped at RESOLVE_EMAIL_CAP emails).
 * When the BE endpoint ships, swap the mutationFn body to:
 *   return fetchApi(`/recipients/resolve`, { method: 'POST', body: JSON.stringify({ emails }) })
 */
export function useResolveEmails() {
  return useMutation({
    mutationFn: async (
      emails: string[],
    ): Promise<{ found: Array<{ id: string; email: string }>; notFound: string[]; capped: boolean }> => {
      if (emails.length === 0) return { found: [], notFound: [], capped: false }

      const capped = emails.length > RESOLVE_EMAIL_CAP
      const toResolve = capped ? emails.slice(0, RESOLVE_EMAIL_CAP) : emails
      const notFound: string[] = capped ? emails.slice(RESOLVE_EMAIL_CAP) : []

      const found: Array<{ id: string; email: string }> = []

      for (const email of toResolve) {
        try {
          const qs = buildQueryString({ search: email, limit: 1, status: ['Active'] })
          const result = await fetchApi<
            | { data: Array<{ id: string; email: string }>; total: number }
            | Array<{ id: string; email: string }>
          >(`/recipients${qs}`)
          const items = Array.isArray(result) ? result : (result.data ?? [])
          const match = items.find(
            (r) => r.email?.toLowerCase() === email.toLowerCase(),
          )
          if (match) {
            found.push({ id: match.id, email: match.email })
          } else {
            notFound.push(email)
          }
        } catch {
          notFound.push(email)
        }
      }

      return { found, notFound, capped }
    },
  })
}

export function useBulkAssignGroups() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (
      data:
        | (BulkTarget & { groupIds: string[] })
        | { recipientIds: string[]; groupIds: string[] },
    ) =>
      recipientsFetch<{ assigned: number }>("bulk-assign-groups", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Adding recipients to groups", "toast.failedBulkAssignGroups", err),
  });
}

export function useBulkRemoveGroups() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (
      data:
        | (BulkTarget & { groupIds: string[] })
        | { recipientIds: string[]; groupIds: string[] },
    ) =>
      recipientsFetch<{ removed: number }>("bulk-remove-groups", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all() });
    },
    onError: (err: Error) =>
      toastRecipientRefusal(t, "Removing recipients from groups", "toast.failedBulkRemoveGroups", err),
  });
}
